import type { ProcessedStudentsResult } from '@/domains/students/student.entity';
import { INITIAL_VISIBLE_COLUMNS } from '@/features/student/const/uiStudentTable.const';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  JornadaFilterState,
  StudentStoreEntity,
  StudentStoreRepository,
  VisibleColumnsState,
} from '../models';
import { optimizedArrayComparison } from '../utils';

/**
 * Valores por defecto del store
 */
const defaultStudentState: StudentStoreEntity = {
  students: [],
  careerOptions: [],
  activeFilter: '',
  searchQuery: '',
  jornadaFilter: 'all',
  visibleColumns: [...INITIAL_VISIBLE_COLUMNS],
  isLoading: false,
};

/**
 * Store de Zustand para gestión de estudiantes
 * Utiliza comparación optimizada de arrays para prevenir re-renders innecesarios
 * Persiste: activeFilter, searchQuery, jornadaFilter y visibleColumns en localStorage
 */
export const useStudentStore = create<StudentStoreRepository>()(
  persist(
    (set, get) => ({
      ...defaultStudentState,

      /**
       * Carga estudiantes y opciones de carrera desde datos procesados
       * Usa optimizedArrayComparison para prevenir actualizaciones innecesarias
       *
       * @param processedData - Datos procesados con lista de estudiantes y opciones de carrera
       */
      loadStudents: (processedData: ProcessedStudentsResult) => {
        const { students } = get();

        if (
          optimizedArrayComparison(
            students,
            processedData.students,
            'enrollment_number',
          )
        ) {
          return;
        }

        set({
          students: processedData.students,
          careerOptions: processedData.careerOptions,
          isLoading: false,
        });
      },

      /**
       * Establece el filtro activo de carrera
       *
       * @param filter - Nombre de la carrera a filtrar
       */
      setFilter: (filter: string) => {
        set({ activeFilter: filter });
      },

      /**
       * Actualiza el término de búsqueda para filtrar estudiantes
       *
       * @param query - Término de búsqueda (nombre, matrícula, etc.)
       */
      setSearchQuery: (query: string) => {
        set({ searchQuery: query });
      },

      /**
       * Actualiza el filtro de jornada (Diurno/Vespertino).
       * Persiste el valor seleccionado entre sesiones.
       *
       * @param value - 'all' para todas las jornadas o un array de keys (`'D'`, `'V'`)
       */
      setJornadaFilter: (value: JornadaFilterState) => {
        set({ jornadaFilter: value });
      },

      /**
       * Actualiza las columnas visibles en la tabla de estudiantes.
       * Persiste la preferencia del usuario entre sesiones.
       *
       * @param value - 'all' para mostrar todas las columnas o un array de keys de columna
       */
      setVisibleColumns: (value: VisibleColumnsState) => {
        set({ visibleColumns: value });
      },

      /**
       * Controla el estado de carga de datos
       *
       * @param loading - true cuando se están cargando datos
       */
      setLoading: (loading: boolean) => set({ isLoading: loading }),

      /**
       * Resetea el store a su estado inicial
       * Limpia lista de estudiantes y filtros
       */
      clearStudents: () => set({ ...defaultStudentState }),
    }),
    {
      name: '@app/student',
      partialize: (state: StudentStoreRepository) => ({
        activeFilter: state.activeFilter,
        searchQuery: state.searchQuery,
        jornadaFilter: state.jornadaFilter,
        visibleColumns: state.visibleColumns,
      }),
    },
  ),
);
